import { getPhasePosition } from '@/allocation/allocation';
import {
    getUnstyledAllocationVariantIndex,
    getUnstyledAllocationVariantLabel,
} from '@/components/AllocationVariantIndex/getFullAllocationVariantIndex';
import { DEFAULT_HIGHLIGHT_VALUE } from '@/components/AllocationVariantSelectionPanel/types';
import { ExternalEventsTriStateProps } from '@/components/ExternalEventsTriState/ExternalEventsTriState.vue';
import { mapStringToSpecializationType } from '@/components/Timeline/allocation/SpecializationMappings';
import { TimelineVariant } from '@/components/Timeline/allocation/types';
import { getDurationInDays } from '@/components/Timeline/logic/durations';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import { getTransparencyVariant } from '@/components/Timeline/timeline';
import { Allocation, LocationTypeCategory, PhaseTypeDatabase } from '@/components/Timeline/Timeline.types';
import {
    ExternalEventDataFragment,
    ExternalEventEventStatus,
    ExternalEventLocationVisibility,
    ExternalEventSeriesCategory,
    ExternalEventSeriesStatus,
    ExternalEventVariantDataFragment,
    ExternalEventVariantStatus,
    TimelineExternalEventAllocationFragment,
    TimelineExternalEventVariantFragment,
} from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { insideRangeCheck } from '@/utils/date';
import { getVariantRoute } from '@/variant/utils';
import { DateTime } from 'luxon';
import { RouteLocationRaw } from 'vue-router';

export const EVENT_CATEGORIES = ['social', 'sport', 'politics', 'other', 'external_construction'];

export const ACTIVE_EXTERNAL_EVENT_STATES = [ExternalEventEventStatus.Fix, ExternalEventEventStatus.Option];
export const ACTIVE_EXTERNAL_EVENT_VARIANT_STATES = [ExternalEventVariantStatus.Fix, ExternalEventVariantStatus.Option];

export function getExternalEventLink(externalEventId: string): RouteLocationRaw {
    return { name: 'external-event-event', params: { eventId: externalEventId } };
}

export function getCreateExternalEventLink(seriesId: string, nameShort: string, nameLong: string): RouteLocationRaw {
    return { name: 'external-event-add', query: { seriesId, nameShort, nameLong } };
}

export function getExternalEventAllocationsRoute(externalEventId: string): RouteLocationRaw {
    return { name: 'external-event-event-allocations-tab', params: { eventId: externalEventId } };
}

export function externalEventSeriesStatusToBadgeVariant(status: string) {
    switch (status as ExternalEventSeriesStatus) {
        case 'active':
            return 'Green';
        case 'completed':
            return 'Gray';
        case 'discontinued':
            return 'Red';
        default:
            return 'Green';
    }
}

export function externalEventEventStatusToBadgeVariant(status: string) {
    // FIXME do not cast here but use proper status types
    switch (status as ExternalEventEventStatus) {
        case 'fix':
            return 'Green';
        case 'option':
            return 'Yellow';
        case 'discontinued':
            return 'Red';
        case 'completed':
            return 'Gray';
        default:
            return 'Green';
    }
}

export function externalEventVariantStatusToBadgeVariant(status: string) {
    switch (status as ExternalEventVariantStatus) {
        case 'fix':
            return 'Green';
        case 'option':
            return 'Yellow';
        case 'discontinued':
            return 'Red';
        default:
            return 'Green';
    }
}

const externalEventSeriesCategoryOrder = [
    'external_construction',
    'public_holiday',
    'holiday',
    'social',
    'world',
    'politics',
    'other',
    'sport',
    'competition',
];

export function getExternalEventSeriesCategoryOptions() {
    const sortedCategories = Object.values(ExternalEventSeriesCategory).sort(
        (a, b) => externalEventSeriesCategoryOrder.indexOf(a) - externalEventSeriesCategoryOrder.indexOf(b),
    );
    return sortedCategories.map((v) => ({
        id: v,
        name: $t('external-event-series-category', { category: v }),
    }));
}

/**
 * A location row in an external-event timeline. External-event locations are always a flat list of leaf
 * rows, so unlike the site timeline's locations there is no nesting, level or type category to carry.
 */
export type ExternalEventTimelineLocation = {
    id: string;
    label: string;
    status: ExternalEventsTriStateProps['status'];
};

export function getStatusFromVisibility(
    visibility: ExternalEventLocationVisibility,
): ExternalEventsTriStateProps['status'] {
    switch (visibility) {
        case 'full':
            return 'Strong';
        case 'less':
            return 'Inner-Strong';
        case 'invisible':
            return 'Invisible';
        default:
            return 'Strong';
    }
}

export function getVisibilityFromStatus(
    status: ExternalEventsTriStateProps['status'],
): ExternalEventLocationVisibility {
    switch (status) {
        case 'Strong':
            return ExternalEventLocationVisibility.Full;
        case 'Inner-Strong':
            return ExternalEventLocationVisibility.Less;
        case 'Invisible':
            return ExternalEventLocationVisibility.Invisible;
        default:
            return ExternalEventLocationVisibility.Full;
    }
}

export function getExternalEventVisibilityVariant(visibilities: ExternalEventLocationVisibility[]) {
    if (visibilities.some((v) => v === ExternalEventLocationVisibility.Full)) {
        return 'Solid' as const;
    } else {
        return 'Outer-Semi-Transparent' as const;
    }
}

export function getExternalEventVariantRoute(eventId: string, variantIndex: number) {
    return { name: 'external-event-event-variants-tab', params: { eventId }, hash: `#v${variantIndex}` };
}

export function getExternalEventVariantLinkTitle(
    event: ExternalEventDataFragment,
    variant: ExternalEventVariantDataFragment,
) {
    const index = getUnstyledAllocationVariantIndex(variant.index);
    if (variant.name === '') {
        return `${index} - ${event.nameShort} - ${event.series?.nameShort}`;
    }
    return `${index} (${variant.name}) - ${event.nameShort} - ${event.series?.nameShort}`;
}

export type TimelineExternalEventVariantAllocationData = {
    id: string;
    index: number;
    name: string;
    referenceAllocationId: string | null;
    isCurrent: boolean;
    event: {
        id: string;
        nameShort: string;
    };
};

export function mapExternalEventAllocationForTimeline(
    variant: TimelineExternalEventVariantAllocationData,
    allocation: TimelineExternalEventAllocationFragment,
    locationTypeCategory: LocationTypeCategory,
    executionPhasePosition: number,
    areAllocationsEditable: boolean,
    seriesCategory: ExternalEventSeriesCategory,
): Allocation {
    const referenceAllocationId = variant.referenceAllocationId ?? null;
    const allocationStart = DateTime.fromISO(allocation.start);
    const allocationEnd = DateTime.fromISO(allocation.end);

    return {
        id: allocation.id,
        allocationType: 'ExternalEvent',
        allocationVariant: {
            id: variant.id,
            index: variant.index,
            label: variant.name,
            target: getVariantRoute('ExternalEvent', variant.event.id, variant.index),
            referenceAllocationId,
        },
        label: $t('allocation-bar-label', {
            groupLabel: variant.event.nameShort,
            variantLabel: getUnstyledAllocationVariantLabel(variant.index, variant.name),
        }),
        variantLabel: getUnstyledAllocationVariantLabel(variant.index, variant.name),
        groupLabel: variant.event.nameShort,
        phases: allocation.phases.nodes.map((phase) => {
            const offsetDays = getDurationInDays(phase.offset);
            const durationDays = getDurationInDays(phase.duration);
            return {
                id: phase.id,
                startDateTime: allocationStart.plus({ days: offsetDays }),
                endDateTime: allocationStart.plus({ days: offsetDays + durationDays }),
                cellSpan: durationDays,
                position: phase.usage.isExecution
                    ? 'Main'
                    : getPhasePosition(executionPhasePosition, phase.usage.position),
                specialization: mapStringToSpecializationType(phase.specializationType),
                offset: phase.offset,
                duration: phase.duration,
                color: mapToPhaseColor(phase.usage.type, seriesCategory),
                usage: {
                    id: phase.usage.id,
                    type: phase.usage.type as PhaseTypeDatabase,
                    position: phase.usage.position,
                },
            };
        }),
        startDateTime: allocationStart,
        endDateTime: allocationEnd,
        locationId: allocation.locationId,
        clickAction: areAllocationsEditable
            ? { type: 'Edit' as const }
            : {
                  type: 'Open-Url' as const,
                  tooltipText: $t('click-to-route-to-masterdata'),
                  target: getExternalEventVariantRoute(variant.event.id, variant.index),
              },
        linkTarget: getExternalEventLink(variant.event.id),
        isOptional: allocation.isOptional,
        variant: getTransparencyVariant(DEFAULT_HIGHLIGHT_VALUE, variant.id, variant.isCurrent),
        isInside: undefined,
        locationTypeCategory,
    };
}

export function mapExternalEventVariantForTimeline(
    variant: TimelineExternalEventVariantFragment,
    timelineStart: DateTime | undefined,
    timelineEnd: DateTime | undefined,
    executionPhasePosition: number,
    areAllocationsEditable: boolean,
): TimelineVariant {
    const allocationsTotalCount = variant.allocations.totalCount;
    if (timelineEnd && timelineStart) {
        return {
            ...variant,
            referenceAllocationId: variant.referenceExternalEventAllocationId ?? null,
            status: variant.status as ExternalEventVariantStatus,
            allocationsTotalCount,
            allocations: {
                nodes: [
                    ...(variant.allocations?.nodes
                        .filter((n) =>
                            insideRangeCheck(
                                DateTime.fromISO(n.start),
                                DateTime.fromISO(n.end),
                                timelineStart,
                                timelineEnd,
                            ),
                        )
                        .map((n) =>
                            mapExternalEventAllocationForTimeline(
                                {
                                    ...variant,
                                    referenceAllocationId: variant.referenceExternalEventAllocationId ?? null,
                                },
                                n,
                                'ExternalLocation',
                                executionPhasePosition,
                                areAllocationsEditable,
                                variant.event.series.category,
                            ),
                        ) ?? []),
                ],
            },
        };
    }
    return {
        ...variant,
        referenceAllocationId: variant.referenceExternalEventAllocationId ?? null,
        status: variant.status as ExternalEventVariantStatus,
        allocationsTotalCount,
        allocations: {
            nodes: [
                ...(variant.allocations?.nodes.map((n) =>
                    mapExternalEventAllocationForTimeline(
                        { ...variant, referenceAllocationId: variant.referenceExternalEventAllocationId ?? null },
                        n,
                        'ExternalLocation',
                        executionPhasePosition,
                        areAllocationsEditable,
                        variant.event.series.category,
                    ),
                ) ?? []),
            ],
        },
    };
}

export const nationalToolTipHeaderMap: Record<ExternalEventSeriesCategory, string> = {
    [ExternalEventSeriesCategory.Holiday]: $t('holiday-national'),
    [ExternalEventSeriesCategory.Other]: $t('event-national'),
    [ExternalEventSeriesCategory.Politics]: $t('politics-national'),
    [ExternalEventSeriesCategory.PublicHoliday]: $t('public-holiday-national'),
    [ExternalEventSeriesCategory.Social]: $t('social-national'),
    [ExternalEventSeriesCategory.Sport]: $t('sport-national'),
    [ExternalEventSeriesCategory.Competition]: $t('competition-national'),
    [ExternalEventSeriesCategory.World]: $t('world-national'),
    [ExternalEventSeriesCategory.ExternalConstruction]: $t('construction-national'),
};
export const internationalToolTipHeaderMap: Record<ExternalEventSeriesCategory, string> = {
    [ExternalEventSeriesCategory.Holiday]: $t('holiday-international'),
    [ExternalEventSeriesCategory.Other]: $t('event-international'),
    [ExternalEventSeriesCategory.Politics]: $t('politics-international'),
    [ExternalEventSeriesCategory.PublicHoliday]: $t('public-holiday-international'),
    [ExternalEventSeriesCategory.Social]: $t('social-international'),
    [ExternalEventSeriesCategory.Sport]: $t('sport-international'),
    [ExternalEventSeriesCategory.Competition]: $t('competition-international'),
    [ExternalEventSeriesCategory.World]: $t('world-international'),
    [ExternalEventSeriesCategory.ExternalConstruction]: $t('construction-international'),
};

export function getExternalEventSeriesStatusLabel(status: string) {
    return $t('external-event-series-status', { status });
}

export function getExternalEventEventStatusLabel(status: string) {
    return $t('external-event-event-status', { status });
}

export function getExternalEventVariantStatusLabel(status: string) {
    return $t('external-event-variant-status', { status });
}

export function getCategoryColor(category: ExternalEventSeriesCategory | undefined) {
    switch (category) {
        case ExternalEventSeriesCategory.ExternalConstruction:
            return 'External-Event-Category-Project';
        case ExternalEventSeriesCategory.Competition:
        case ExternalEventSeriesCategory.World:
            return 'External-Event-Category-Event';
        default:
            return 'External-Event-Category-External-Event';
    }
}
